import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "./Reveal";
import { Plus, Volume2, VolumeX } from "lucide-react";

const BANNER_SPOTS = [
  { x: 40.2, y: 7.9, label: "Brand Consistency", note: "Vai pircējs atcerēsies, kas viņam pārdeva?" },
  { x: 78.0, y: 13.1, label: "Human Focus / Visual Hook", note: "Vai galvenais vizuālis piesaista uzmanību? Vai palīdz pārdot? Vai tikai aizņem vietu?" },
  { x: 39.7, y: 37.3, label: "Creative Impact", note: "Vai vizuālis ir pietiekami spēcīgs, lai apturētu scroll?" },
  { x: 76.6, y: 63.2, label: "Message Clarity", note: "Vai pircējs 2-3 sekundēs saprot, ko Jūs viņam pārdodat?" },
  { x: 52.0, y: 78.5, label: "Value Proposition", note: "Vai pircējam ir skaidrs un vai ir pietiekams iemesls, kāpēc izvēlēties Jūsu piedāvājumu?" },
];

const VIDEO_SPOTS = [
  { x: 50, y: 10, label: "Hook Strength / First 3 Seconds", note: "Stop-scroll. Vai video pirmās 2-3 sekundes aptur uzmanību un rada vēlmi skatīties tālāk?" },
  { x: 65, y: 28, label: "Message Clarity", note: "Vai skatītājs ātri saprot, ko Jūs piedāvājat un kāpēc tas viņam ir aktuāli?" },
  { x: 22, y: 46, label: "Pacing & Engagement", note: "Vai video temps un kadru maiņa notur uzmanību līdz galvenajam vēstījumam?" },
  { x: 45, y: 56, label: "Creative Impact", note: "Vai vizuālais risinājums izceļas, paliek atmiņā un palīdz pārdot?" },
  { x: 70, y: 68, label: "Value Proposition / Proof", note: "Vai video dod pietiekami spēcīgu iemeslu izvēlēties Jūsu piedāvājumu un tam noticēt?" },
  { x: 50, y: 90, label: "CTA Effectiveness", note: "Vai video skaidri un pārliecinoši virza skatītāju uz nākamo soli?" },
];

const Hotspot = ({ spot, index, testid }) => {
  const [open, setOpen] = useState(false);

  if (spot.plain) {
    return (
      <span
        data-testid={testid}
        className="absolute flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[#ED1C27] text-white shadow-[0_0_0_5px_rgba(255,87,51,0.3)]"
        style={{ left: `${spot.x}%`, top: `${spot.y}%` }}
      >
        <Plus className="h-3.5 w-3.5" />
        <span className="absolute inset-0 rounded-full bg-[#ED1C27] animate-ping opacity-30" />
      </span>
    );
  }

  return (
    <div className="absolute" style={{ left: `${spot.x}%`, top: `${spot.y}%` }}>
      <button
        data-testid={testid}
        onClick={() => setOpen(!open)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="relative flex items-center justify-center h-8 w-8 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#ED1C27] text-white font-bold shadow-[0_0_0_6px_rgba(255,87,51,0.3)] hover:scale-110 transition-transform duration-200"
        aria-label={spot.label}
      >
        <Plus className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-45" : ""}`} />
        <span className="absolute inset-0 rounded-full bg-[#ED1C27] animate-ping opacity-30" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            className={`absolute z-20 w-52 rounded-sm bg-white border border-black/10 shadow-xl p-4 pointer-events-none ${
              spot.x > 62 ? "right-4" : spot.x < 38 ? "left-4" : "left-1/2 -ml-[6.5rem]"
            } ${spot.y > 60 ? "bottom-4" : "top-4"}`}
            data-testid={`${testid}-tooltip`}
          >
            <div className="flex items-center justify-between gap-2">
              <p className="font-display font-bold text-sm tracking-tight text-[#003E75]">{spot.label}</p>
              {spot.score && <span className="text-xs font-bold text-[#ED1C27] shrink-0">{spot.score}</span>}
            </div>
            <p className="mt-1.5 text-xs text-[#003E75]/60 leading-relaxed">{spot.note}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ExampleCard = ({ title, tag, img, video, spots, testid, vertical }) => {
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);

  const toggleSound = () => {
    if (videoRef.current) {
      videoRef.current.muted = !muted;
      if (!muted) videoRef.current.pause();
      else videoRef.current.play();
      setMuted(!muted);
    }
  };

  return (
  <Reveal>
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight text-white">{title}</h3>
        <span className="text-xs uppercase tracking-[0.2em] text-white/40">{tag}</span>
      </div>
      <div className={`relative rounded-sm overflow-hidden border border-white/15 bg-[#002B54] ${vertical ? "aspect-[9/16] max-w-sm" : "aspect-square"}`}>
        {video ? (
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 h-full w-full object-cover"
            data-testid={`${testid}-video`}
          >
            <source src={video.replace(".mp4", ".webm")} type="video/webm" />
            <source src={video} type="video/mp4" />
          </video>
        ) : (
          <img src={img} alt={title} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
        )}
        {spots.map((s, i) => (
          <Hotspot key={i} spot={s} index={i} testid={`${testid}-hotspot-${i}`} />
        ))}
        {video && (
          <button
            data-testid={`${testid}-sound-toggle`}
            onClick={toggleSound}
            className="absolute bottom-4 right-4 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-[#003E75]/70 backdrop-blur-md border border-white/20 text-white hover:bg-[#ED1C27] transition-colors duration-300"
            aria-label={muted ? "Ieslēgt skaņu" : "Izslēgt skaņu"}
          >
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
        )}
      </div>
      <p className="mt-3 text-xs text-white/35">Piemērs ar ilustratīvu vērtējumu - uzbrauciet uz punktiem.</p>
    </div>
  </Reveal>
  );
};

export default function AuditExamples() {
  return (
    <section id="piemeri" data-testid="examples-section" className="bg-[#003E75]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
        <Reveal className="mb-4">
          <span className="text-xs font-semibold uppercase tracking-[0.3em] text-[#ED1C27]">Audits praksē</span>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-display font-black tracking-tighter leading-[1.05] text-3xl sm:text-4xl lg:text-5xl text-white" data-testid="examples-heading">
            Kā izskatās banera un video{" "}
            <span className="text-white/35">izvērtējums</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="mt-6 max-w-3xl">
          <p className="text-white/55 text-base leading-relaxed">
            Katru kreatīvu izvērtējam pa elementiem - no pirmās acu mirkļa līdz pēdējam kadram.
          </p>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-2 gap-10 items-start">
          <ExampleCard title="Banera audits" tag="Display / Social" img="/assets/hero-banner.jpg" spots={BANNER_SPOTS} testid="banner-example" />
          <ExampleCard title="Video audits" tag="UGC / Reels" video="/assets/example-video.mp4" spots={VIDEO_SPOTS} testid="video-example" vertical />
        </div>
      </div>
    </section>
  );
}
